import { Component, Vue, Watch } from 'vue-property-decorator';
import { Action, Getter, Mutation } from 'vuex-class';
import {
  GET_STATISTICS,
  GET_STATISTICS_OFFICES,
  SET_STATISTICS,
  STATISTICS,
  STATISTICS_ERRORS,
  STATISTICS_OFFICES,
} from '@/types/managerForm.types';
import MockData from '@/globals/mock';
import StatisticMockData from '@/globals/statisticsMosk.json';
import { StoreNamespace } from '@/globals/StoreNamespaceGlobal';
import { Form101StatisticsModel } from '@/models/manager/Form101Statistics.model';
import { OfficesModel } from '@/models/Offices.model';
import { GridColumn } from '@/models/GridColumn.model';
import { Grid } from '@/models/Grid.model';
import { Utils } from '@/globals/Utils';

@Component({})
export default class StatisticsComponent extends Vue {
  @Action(GET_STATISTICS, StoreNamespace.MANAGER) public getStatistics;
  @Action(GET_STATISTICS_OFFICES, StoreNamespace.MANAGER) public getStatisticsOffices;

  @Mutation(SET_STATISTICS, StoreNamespace.MANAGER) public setStatistics;

  @Getter(STATISTICS, StoreNamespace.MANAGER) public statistics!: Form101StatisticsModel[];
  @Getter(STATISTICS_OFFICES, StoreNamespace.MANAGER) public statisticsOffices!: OfficesModel[];
  @Getter(STATISTICS_ERRORS, StoreNamespace.MANAGER) public statisticsErrors!: string[];

  public chartData: any = {};
  public barOptions: object = {};
  public doughnutOptions: object = {};
  public selectedOffice: number | string = 0;
  public selectedOfficeStatistic: Form101StatisticsModel = {} as Form101StatisticsModel;
  public selectedOfficeStatisticGrid: Grid<Form101StatisticsModel> = {} as Grid;

  public totalElectronicForms: string = '0';
  public totalManualForms: string = '0';
  public statisticsDate: Date | null | string = null;

  private columns: GridColumn[] = [];
  private mockData: any = MockData.statics;

  public mounted() {
    this.getStatisticsOffices();
    this.selectedOffice = 0;
    this.selectedOfficeStatistic = {} as Form101StatisticsModel;
    this.selectedOfficeStatisticGrid = {} as Grid;
    this.initColumns();
    this.getStatistics();

  }

  @Watch('statistics')
  public fillData() {
    let unfilledCount = 0;
    let employeeFillCount = 0;
    let rejectedCount = 0;
    let employeeConfirmCount = 0;
    let managerConfirmedCount = 0;
    let totalElectronicFormsCount = 0;
    let totalManualFormsCount = 0;
    let manualUpdateIptCount = 0;
    let updateIptWaitingForManagerCount = 0;
    let updateIptCompleteCount = 0;
    let result;
    this.statisticsDate = null;
    this.totalElectronicForms = '0';
    this.totalManualForms = '0';
    if (typeof this.selectedOffice === 'number' && this.selectedOffice) {
      result = this.statistics.filter(value => value.officeNum && value.officeNum === this.selectedOffice);
    } else {
      result = this.statistics;
    }
    for (const data of result) {
      totalElectronicFormsCount += data.totalElectronicForms;
      totalManualFormsCount += data.totalManualForms;
      unfilledCount += data.unfilledCount;
      employeeFillCount += data.employeeFillCount;
      rejectedCount += data.rejectedCount;
      employeeConfirmCount += data.employeeConfirmCount;
      managerConfirmedCount += data.managerConfirmedCount;
      updateIptCompleteCount += data.updateIptCompleteCount;
      manualUpdateIptCount += data.manualUpdateIptCount;
      updateIptWaitingForManagerCount += data.updateIptWaitingForManagerCount;
    }
    this.totalElectronicForms = this.addComma(totalElectronicFormsCount);
    this.totalManualForms = this.addComma(+totalManualFormsCount);
    if (result.length) {

      if (typeof result[0].statisticsDate === 'string') {
        this.statisticsDate = result[0].statisticsDate;
      } else {
        if (this.statisticsDate !== null && result[0].statisticsDate < Utils.dateToString(this.statisticsDate)) {
          this.statisticsDate =
            result[0].statisticsDate ? new Date(Utils.stringToDate('' + result[0].statisticsDate)) : null;
        } else if (this.statisticsDate === null) {
          this.statisticsDate = new Date(Utils.stringToDate('' + result[0].statisticsDate));
        }
      }

      this.chartData = {
        datasets: [{
          data: [
            unfilledCount,
            employeeFillCount,
            rejectedCount,
            employeeConfirmCount,
            managerConfirmedCount,
            manualUpdateIptCount,
            updateIptWaitingForManagerCount,
            updateIptCompleteCount,
          ],
          backgroundColor: this.mockData.backgroundColor,
        }],
        labels: this.mockData.staticsLabels,
      };
      this.barOptions = {
        legend: { display: false },
        scales: {
          yAxes: [{
            ticks: {
              callback: (value) => {
                return this.addComma(value);
              },
            },
          }],
          xAxes: [{
            maxBarThickness: 80,
            ticks: {
              beginAtZero: false,
              reverse: false,
            },
            gridLines: {
              drawBorder: true,
              display: false,
            },
          },
          ],
        },
        layout: {
          padding: {
            left: 0,
            right: 0,
            top: 5,
            bottom: 0,
          },
        },
        drawBorder: true,
        responsive: true,
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            label: (tooltipItem, chart) => {
              return this.addComma(chart.datasets[0].data[tooltipItem.index]);
            },
          },
        },
      };
      this.doughnutOptions = {
        legend: { position: 'right' },
        responsive: true,
        tooltips: {
          callbacks: {
            label: (tooltipItem, chart) => {
              return this.addComma(chart.datasets[0].data[tooltipItem.index]);
            },
          },
        },
      };
    }
    this.selectedOfficeStatisticGrid = { results: result, resultCount: result.length } as Grid;
  }

  private addComma(value: number): string {
    if (isNaN(value)) {
      return '0';
    }
    return value ? value.toFixed(0).replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1,') : '0';
  }

  private initColumns() {
    this.columns.push({
      property: 'officeName', subProperty: 'officeNum', header: 'statistics.officeName', hideSorting: true,
    } as GridColumn);
    this.columns.push({
      property: 'population', header: 'statistics.population', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'filledPartPercent', header: 'statistics.filledPartPercent',
      hideSorting: true, classProperty: 'status', isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'totalElectronicForms', header: 'statistics.totalElectronicForms', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'totalManualForms', header: 'statistics.totalManualForms', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'unfilledCount', header: 'statistics.unfilledCount', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'employeeFillCount', header: 'statistics.employeeFillCount', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'rejectedCount', header: 'statistics.rejectedCount', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'employeeConfirmCount', header: 'statistics.employeeConfirmCount', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'managerConfirmedCount', header: 'statistics.managerConfirmedCount', hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'manualUpdateIptCount', header: 'statistics.manualUpdateIptCount',
      hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'updateIptWaitingForManagerCount', header: 'statistics.updateIptWaitingForManagerCount',
      hideSorting: true, isNumber: true,
    } as GridColumn);
    this.columns.push({
      property: 'updateIptCompleteCount', header: 'statistics.updateIptCompleteCount', hideSorting: true,
      isNumber: true,
    } as GridColumn);

  }
}
